import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { DimensionPanel } from '../../../components/builder/DimensionPanel';
import { Viewer3D } from '../../../components/builder/Viewer3D';
import type { RuleOverrides } from '../../../data/ruleRegistry';
import type { BuildGuideText, BuildProgress, ProjectBuildGuide } from '../../../domain/build/getProjectBuildGuide';
import { getBuildStepSupplies } from '../../../domain/build/getBuildStepSupplies';
import type { FinishPriceDeltas } from '../../../domain/finishes/finishPriceDeltas';
import type { FinishConfig } from '../../../domain/finishes/types';
import type { SiteConditions } from '../../../domain/foundation/siteConditions';
import type { WallOpening } from '../../../domain/framing/generateFramedWall';
import { calculateHeaterRequirements } from '../../../domain/heater/calculateHeaterRequirements';
import type { IndoorRoomConfig } from "../../../domain/indoor/indoorRoom";
import type { InteriorLayout } from '../../../domain/interior';
import type { ClimateProfile, LocationMode, Market } from '../../../domain/location/types';
import type { SaunaRotationDeg } from '../../../domain/viewer/saunaRotation';
import type { TimeOfDayValue } from '../../../domain/viewer/timeOfDay';
import type {
  BuildInput,
  CalculationResult,
  DeckConfig,
  MaterialViewMode,
  RoofConfig,
  SaunaEngine,
  TwoRoomConfig,
  VisibilityFlags,
} from '../../../engines/types';
import { useT } from '../../../i18n';
import { translateBuildGuideText } from '../../../i18n/buildGuide';
import { getViewportClass, trackConversionEvent } from '../../../lib/conversionTelemetry';
import type { TwoRoomPresetId } from '../../../store/twoRoomPresets';
import type { SaunaType } from '../../../types/design';
import type { BuilderRunningCostSummary } from '../runningCostSummary';
import type { BuilderWorkspace } from '../types';
import type { BuilderWorkspaceMeta } from '../workspaces';
import { collectSafetyFlags } from '../safetyFlags';
import { SafetyFlagNotice } from './SafetyFlagNotice';

const BBRPane = lazy(() =>
  import('../../../components/builder/tabs/BBRPane').then(({ BBRPane }) => ({ default: BBRPane })),
);
const CutPane = lazy(() =>
  import('../../../components/builder/tabs/CutPane').then(({ CutPane }) => ({ default: CutPane })),
);
const FoundationDiagramPane = lazy(() =>
  import('../../../components/builder/tabs/FoundationDiagramPane').then(({ FoundationDiagramPane }) => ({ default: FoundationDiagramPane })),
);
const FramingDiagramPane = lazy(() =>
  import('../../../components/builder/tabs/FramingDiagramPane').then(({ FramingDiagramPane }) => ({ default: FramingDiagramPane })),
);
const InstructionsPane = lazy(() =>
  import('../../../components/builder/tabs/InstructionsPane').then(({ InstructionsPane }) => ({ default: InstructionsPane })),
);
const MaterialPane = lazy(() =>
  import('../../../components/builder/tabs/MaterialPane').then(({ MaterialPane }) => ({ default: MaterialPane })),
);
const PricePane = lazy(() =>
  import('../../../components/builder/tabs/PricePane').then(({ PricePane }) => ({ default: PricePane })),
);
/* Lazy for the same reason the panes are: the plan view pulls the interior
   resolver and its own stylesheet, and the 3D viewer is the default surface. */
const PlanViewPanel = lazy(() =>
  import('../../../components/builder/PlanView/PlanViewPanel').then(({ PlanViewPanel }) => ({
    default: PlanViewPanel,
  })),
);
const InteriorLayoutEditor = lazy(() =>
  import('../../../components/builder/InteriorLayoutEditor').then(({ InteriorLayoutEditor }) => ({ default: InteriorLayoutEditor })),
);

function WorkspaceLoadingFallback() {
  const t = useT();

  return (
    <div className="builder-workspace-panel builder-tab-content" role="status" aria-live="polite">
      {t.common_loading}
    </div>
  );
}

interface WorkspaceRouterProps {
  activeWorkspace: BuilderWorkspace;
  /** Opens Safety & planning from the design panel's flag notice. */
  onReviewSafety?: () => void;
  cutProjectStorageKey?: string;
  onJumpToWorkspace?: (workspace: BuilderWorkspace) => void;
  activeWorkspaceMeta: BuilderWorkspaceMeta;
  buildInput: BuildInput;
  visibility: VisibilityFlags;
  dimensions: { width: number; length: number };
  partitionSplit: number;
  twoRoomConfig: TwoRoomConfig;
  timeOfDay: TimeOfDayValue;
  rotationDeg: SaunaRotationDeg;
  openings: WallOpening[];
  deckConfig: DeckConfig | null;
  roofConfig: RoofConfig;
  thermalInputs: BuildInput['thermalInputs'];
  finishes: FinishConfig;
  /** Per-option cost delta for the Karaktar swatches, from the live BOM. */
  finishPriceDeltas: FinishPriceDeltas;
  siteConditions: SiteConditions;
  market: Market;

  locationMode: LocationMode;
  climateProfile: ClimateProfile;
  postalCode: string;
  setDimensions: (dimensions: { width: number; length: number }) => void;
  setPartitionSplit: (split: number) => void;
  setTwoRoomConfig: (config: Partial<TwoRoomConfig>) => void;
  setTwoRoomPreset: (preset: TwoRoomPresetId) => void;
  setTimeOfDay: (timeOfDay: TimeOfDayValue) => void;
  setRotationDeg: (rotationDeg: number) => void;
  setOpenings: (openings: WallOpening[] | ((previous: WallOpening[]) => WallOpening[])) => void;
  setDeckConfig: (config: DeckConfig | null) => void;
  setRoofConfig: (config: RoofConfig) => void;
  setThermalInputs: (inputs: BuildInput['thermalInputs']) => void;
  setFinishes: (finishes: Partial<FinishConfig>) => void;
  setWallHeightM: (wallHeightM: number) => void;
  indoorRoom: IndoorRoomConfig | null;
  setIndoorRoom: (indoorRoom: IndoorRoomConfig) => void;
  setSiteConditions: (conditions: SiteConditions) => void;
  setClimateProfile: (profile: ClimateProfile) => void;
  setPostalCode: (postalCode: string) => void;
  buildProgress: BuildProgress;
  /** The market-aware guide from useBuilderDerivedData; the Build badge counts the same steps. */
  buildGuide: ProjectBuildGuide;
  setBuildProgress: (v: BuildProgress | ((prev: BuildProgress) => BuildProgress)) => void;
  calcData: CalculationResult;
  heater: ReturnType<typeof calculateHeaterRequirements>;
  /** Live heat-up/session-cost readout; null when it cannot be derived. */
  runningCost?: BuilderRunningCostSummary | null;
  overrides: RuleOverrides;
  engine: SaunaEngine;
  materialViewMode: MaterialViewMode;
  onMaterialViewModeChange: (mode: MaterialViewMode) => void;
  isDraggingWindow: { widthMm: number; heightMm: number } | null;
  onStartWindowDrag: (widthMm: number, heightMm: number) => void;
  onWindowPlaced: (opening: WallOpening) => void;
  onOpeningMoved: (opening: WallOpening) => void;
  onPartitionSplitDragged: (partitionSplit: number) => void;
  onDragCancelled: () => void;
  onSnapshotReady: (snapshotGetter: () => string | null) => void;
  onVisibilityChange: (visibility: VisibilityFlags) => void;
  renderError: string | null;
  designId: string | null;
  type: SaunaType;
  /** Upper bound on the scale-figure count, from the wizard's size tier. */
  scaleFigureCapacityHint?: number | null;
  setInteriorLayout: (layout: InteriorLayout | null) => void;
}

export function WorkspaceRouter({
  activeWorkspace,
  onReviewSafety,
  onJumpToWorkspace,
  cutProjectStorageKey,
  activeWorkspaceMeta,
  buildInput,
  visibility,
  dimensions,
  partitionSplit,
  twoRoomConfig,
  timeOfDay,
  rotationDeg,
  openings,
  deckConfig,
  roofConfig,
  thermalInputs,
  finishes,
  finishPriceDeltas,
  setFinishes,
  setWallHeightM,
  indoorRoom,
  setIndoorRoom,
  siteConditions,
  market,
  locationMode,
  climateProfile,
  postalCode,
  setDimensions,
  setPartitionSplit,
  setTwoRoomConfig,
  setTwoRoomPreset,
  setTimeOfDay,
  setRotationDeg,
  setOpenings,
  setDeckConfig,
  setRoofConfig,
  setThermalInputs,
  setSiteConditions,
  setClimateProfile,
  setPostalCode,
  buildProgress,
  buildGuide,
  setBuildProgress,
  calcData,
  heater,
  runningCost,
  overrides,
  engine,
  materialViewMode,
  onMaterialViewModeChange,
  isDraggingWindow,
  onStartWindowDrag,
  onWindowPlaced,
  onOpeningMoved,
  onPartitionSplitDragged,
  onDragCancelled,
  onSnapshotReady,
  onVisibilityChange,
  renderError,
  designId,
  type,
  scaleFigureCapacityHint,
  setInteriorLayout,
}: WorkspaceRouterProps) {
  /* Which of the two editing surfaces is on screen. A VIEW preference, not
     design data: it stays local so it never enters undo/redo or the saved
     design (same reasoning as the unit-system toggle). */
  const [viewMode, setViewMode] = useState<'3d' | 'plan'>('3d');
  const [selectedBenchId, setSelectedBenchId] = useState<string | null>(null);
  const handleStartWindowDrag = (widthMm: number, heightMm: number) => {
    setSelectedBenchId(null);
    setViewMode('3d');
    onStartWindowDrag(widthMm, heightMm);
  };
  const t = useT();
  const renderGuideText = (value: BuildGuideText) => translateBuildGuideText(value, t);
  const [activeDesignSection, setActiveDesignSection] = useState('dimensions');


  useEffect(() => {
    if (activeWorkspace !== 'build') return;
    trackConversionEvent('guide_opened', {
      market,
      sauna_type: type,
      guide_surface: getViewportClass() === 'mobile' ? 'mobile' : 'desktop',
    }, { once: 'guide-opened' });
  }, [activeWorkspace, market, type]);

  const handleToggleBuildStep = useCallback((stepId: string) => {
    const step = buildGuide.steps.find((candidate) => candidate.id === stepId);
    if (!step) return;

    const previous = buildProgress[stepId];
    const isComplete = previous?.completed || (
      step.checkpoints.length > 0
        && step.checkpoints.every((checkpoint) => (previous?.completedCheckpointIds ?? []).includes(checkpoint.id))
    );
    if (!isComplete) {
      trackConversionEvent('guide_gate_completed', {
        market,
        sauna_type: type,
        guide_surface: getViewportClass() === 'mobile' ? 'mobile' : 'desktop',
        guide_step_id: stepId,
      });
    }

    setBuildProgress((current) => {
      return {
        ...current,
        [stepId]: {
          completed: !isComplete,
          completedCheckpointIds: !isComplete ? step.checkpoints.map((checkpoint) => checkpoint.id) : [],
        },
      };
    });
  }, [buildGuide.steps, buildProgress, market, setBuildProgress, type]);

  const handleToggleBuildCheckpoint = useCallback((stepId: string, checkpointId: string) => {
    const step = buildGuide.steps.find((candidate) => candidate.id === stepId);
    if (!step) return;

    const wasComplete = (buildProgress[stepId]?.completedCheckpointIds ?? []).includes(checkpointId);
    if (!wasComplete) {
      trackConversionEvent('guide_checkpoint_completed', {
        market,
        sauna_type: type,
        guide_surface: getViewportClass() === 'mobile' ? 'mobile' : 'desktop',
        guide_step_id: stepId,
        guide_checkpoint_id: checkpointId,
      });
    }

    setBuildProgress((current) => {
      const completedCheckpointIds = current[stepId]?.completedCheckpointIds ?? [];
      const nextIds = wasComplete
        ? completedCheckpointIds.filter((id) => id !== checkpointId)
        : [...completedCheckpointIds, checkpointId];

      return {
        ...current,
        [stepId]: {
          completed: step.checkpoints.length > 0
            && step.checkpoints.every((checkpoint) => nextIds.includes(checkpoint.id)),
          completedCheckpointIds: nextIds,
        },
      };
    });
  }, [buildGuide.steps, buildProgress, market, setBuildProgress, type]);

  return (
    <main className="builder-workspace" aria-label={activeWorkspaceMeta.label}>
      {activeWorkspace === 'design' && (
        <div className="builder-design-layout">
          <div className="builder-design-panel">
            <div hidden={selectedBenchId !== null}>
              {onReviewSafety && (
                <SafetyFlagNotice
                  flags={collectSafetyFlags(calcData, market, t)}
                  onReview={onReviewSafety}
                />
              )}
              {type !== 'tunna' && <button type="button" className="btn btn-secondary" onClick={() => setSelectedBenchId('')}>{t.interior_manage}</button>}
              <DimensionPanel
                interiorLayout={calcData?.interiorLayout}
                dimensions={dimensions}
                partitionSplit={partitionSplit}
                twoRoomConfig={twoRoomConfig}
                openings={openings}
                deckConfig={deckConfig}
                roofConfig={roofConfig}
                thermalInputs={thermalInputs}
                finishes={finishes}
                finishPriceDeltas={finishPriceDeltas}
                runningCost={runningCost}
                heaterModelFit={heater.modelFit}
                siteConditions={siteConditions}
                market={market}
                locationMode={locationMode}
                climateProfile={climateProfile}
                postalCode={postalCode}
                saunaType={type}
                onDimensionsChange={setDimensions}
                onPartitionSplitChange={setPartitionSplit}
                onTwoRoomConfigChange={setTwoRoomConfig}
                onTwoRoomPresetChange={setTwoRoomPreset}
                onOpeningsChange={setOpenings}
                onDeckConfigChange={setDeckConfig}
                onRoofConfigChange={setRoofConfig}
                onThermalInputsChange={setThermalInputs}
                onFinishesChange={setFinishes}
                wallHeightM={buildInput.wallHeightM ?? null}
                onWallHeightChange={setWallHeightM}
                indoorRoom={indoorRoom}
                onIndoorRoomChange={setIndoorRoom}
                onSiteConditionsChange={setSiteConditions}
                onClimateProfileChange={setClimateProfile}
                onPostalCodeChange={setPostalCode}
                onStartWindowDrag={handleStartWindowDrag}
                isDraggingWindow={!!isDraggingWindow}
                onActiveSectionChange={setActiveDesignSection}
              />
            </div>
            {selectedBenchId !== null && (
              <Suspense fallback={<WorkspaceLoadingFallback />}>
                <InteriorLayoutEditor
                  key={`${type}:${selectedBenchId}`}
                  buildInput={buildInput}
                  selectedBenchId={selectedBenchId}
                  onSelectBench={setSelectedBenchId}
                  onLayoutChange={setInteriorLayout}
                  onClose={() => setSelectedBenchId(null)}
                />
              </Suspense>
            )}
          </div>

          <div className="builder-viewer-surface">
            <div className="viewer-badges">
              {designId && (
                <div className="badge-autosave">
                  <svg className="badge-check-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M2.5 6.5L5 9L9.5 3.5" stroke="#65d46e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t.badge_autosaved}
                </div>
              )}
            </div>

            {/* biome-ignore lint/a11y/useSemanticElements: a <fieldset> would
                impose legend/border chrome on a floating two-button switch over
                the viewer; role="group" + aria-label is the intent. */}
            <div
              className="builder-viewer-modes"
              role="group"
              aria-label={t.plan_view_modeGroup}
            >
              <button
                type="button"
                className={`builder-viewer-mode-btn${viewMode === '3d' ? ' is-active' : ''}`}
                aria-pressed={viewMode === '3d'}
                onClick={() => setViewMode('3d')}
              >
                {t.plan_view_mode3d}
              </button>
              <button
                type="button"
                className={`builder-viewer-mode-btn${viewMode === 'plan' ? ' is-active' : ''}`}
                aria-pressed={viewMode === 'plan'}
                onClick={() => {
                  if (isDraggingWindow) onDragCancelled();
                  setViewMode('plan');
                }}
              >
                {t.plan_view_modePlan}
              </button>
            </div>

            {viewMode === 'plan' ? (
              <div className="builder-viewer">
                <Suspense fallback={<WorkspaceLoadingFallback />}>
                  <PlanViewPanel
                    buildInput={buildInput}
                    selectedBenchId={selectedBenchId}
                    onSelectBench={setSelectedBenchId}
                    onLayoutChange={setInteriorLayout}
                    onOpeningsChange={(next) =>
                      setOpenings(next as WallOpening[])
                    }
                  />
                </Suspense>
              </div>
            ) : (
            <div className="builder-viewer">
              <Viewer3D
                buildInput={buildInput}
                visibility={visibility}
                engine={engine}
                onVisibilityChange={onVisibilityChange}
                timeOfDay={timeOfDay}
                onTimeOfDayChange={setTimeOfDay}
                rotationDeg={rotationDeg}
                onRotationChange={setRotationDeg}
                onSnapshotReady={onSnapshotReady}
                isDraggingWindow={isDraggingWindow}
                isEditingOpenings={activeDesignSection === 'openings'}
                onWindowPlaced={onWindowPlaced}
                onOpeningMoved={onOpeningMoved}
                onPartitionSplitChange={onPartitionSplitDragged}
                onDragCancelled={onDragCancelled}
                scaleFigureCapacityHint={scaleFigureCapacityHint}
                /* The SAME action the plan view commits through — a 3D drag and a
                   plan drag are one edit seen two ways (issue 133). */
                onInteriorLayoutChange={setInteriorLayout}
                onSelectBench={setSelectedBenchId}
              />

            </div>
            )}
          </div>
        </div>
      )}

      <Suspense fallback={<WorkspaceLoadingFallback />}>
        {activeWorkspace === 'shopping' && (
          <div className="builder-workspace-panel builder-tab-content">
            <MaterialPane
              rows={calcData.materials}
              materialsByPhase={calcData.materialsByPhase}
              weight={calcData.weight}
              electricalBom={calcData.electricalBom}
              viewMode={materialViewMode}
              onViewModeChange={onMaterialViewModeChange}
              onJumpToCutlist={onJumpToWorkspace ? () => onJumpToWorkspace("cutlist") : undefined}
              onJumpToBuild={onJumpToWorkspace ? () => onJumpToWorkspace("build") : undefined}
              onJumpToPricing={onJumpToWorkspace ? () => onJumpToWorkspace("pricing") : undefined}
            />
          </div>
        )}

        {activeWorkspace === 'cutlist' && (
          <div className="builder-workspace-panel builder-tab-content">
            <CutPane rows={calcData.cuts} materialsByPhase={calcData.materialsByPhase}
              projectStorageKey={cutProjectStorageKey}
              onJumpToShopping={onJumpToWorkspace ? () => onJumpToWorkspace("shopping") : undefined}
              onJumpToBuild={onJumpToWorkspace ? () => onJumpToWorkspace("build") : undefined}
            />
          </div>
        )}

        {activeWorkspace === 'pricing' && (
          <div className="builder-workspace-panel builder-tab-content">
            <PricePane
              rows={calcData.materials}
              onJumpToShopping={onJumpToWorkspace ? () => onJumpToWorkspace("shopping") : undefined}
              saunaType={type}
              width={dimensions.width}
              length={dimensions.length}
              openings={openings}
              thermalInputs={thermalInputs}
              pricingSummary={calcData.pricingSummary ?? {
                materialCostSek: 0,
                diyBudgetSek: 0,
                marketPackageSek: 0,
                professionalCostSek: 0,
                allowances: [],
                comparatorNoteKey: 'domain_pricing_comparatorNote',
              }}
              productRecommendations={calcData.productRecommendations}
              chimney={calcData.chimney}
            />
          </div>
        )}

        {activeWorkspace === 'build' && (
          <div className="builder-workspace-panel builder-tab-content builder-build-panel">
          <section className="builder-build-kickoff">
            {market === 'intl' && (
              <div className="alert-box">
                <strong>{t.build_disclaimerTitle}</strong>
                <br />
                {t.build_disclaimerBody}
              </div>
            )}
            {!designId && (
              <p className="builder-progress-local-note">{t.build_progressLocalNote}</p>
            )}
            <details className="builder-build-project-details">
              <summary>{t.instr_projectBasis}</summary>
              <div>
              <p className="builder-summary-description">
                {renderGuideText(buildGuide.kickoff.summaryText ?? buildGuide.kickoff.summary)}
              </p>

            </div>
            <div className="builder-build-kpis">
              {buildGuide.kickoff.highlights.map((item) => (
                <div key={item.label}>
                  <span>{renderGuideText(item.labelText ?? item.label)}</span>
                  <strong>{renderGuideText(item.valueText ?? item.value)}</strong>
                </div>
              ))}
            </div>
            </details>
          </section>
          <section className="builder-build-section">
            <InstructionsPane
              suppliesByStep={Object.fromEntries(buildGuide.steps.map(step => [step.id, getBuildStepSupplies(step.id, calcData)]))}
              onJumpToShopping={onJumpToWorkspace ? () => onJumpToWorkspace("shopping") : undefined}
              onJumpToCutlist={onJumpToWorkspace ? () => onJumpToWorkspace("cutlist") : undefined}
              guide={buildGuide}
              progress={buildProgress}
              onToggleStep={handleToggleBuildStep}
              onToggleCheckpoint={handleToggleBuildCheckpoint}
              buildStepViewContext={{
                buildInput,
                engine,
                saunaType: type,
              }}
              foundationSlot={
                calcData.foundation ? (
                  <FoundationDiagramPane
                    foundation={calcData.foundation}
                    width={dimensions.width}
                    length={dimensions.length}
                  />
                ) : undefined
              }
              framingSlot={
                <FramingDiagramPane
                  width={dimensions.width}
                  length={dimensions.length}
                  openings={openings}
                  partitionSplit={partitionSplit}
                  wallHeightM={buildInput.wallHeightM}
                />
              }
            />
          </section>
          </div>
        )}

        {activeWorkspace === 'rules' && (
          <div className="builder-workspace-panel builder-tab-content">
            <BBRPane
 openingValidation={calcData.openingValidation}
              heater={heater}
              heaterType={thermalInputs.heaterType}
              ventilation={calcData.ventilation}
              ergonomics={calcData.ergonomics}
              roofJoists={calcData.roofJoists}
              constructionBasis={calcData.constructionBasis}
              roofAssembly={calcData.roofAssembly}
              glassPenalty={calcData.glassPenalty}
              permit={calcData.permit}
              placement={calcData.placement}
              drainage={calcData.drainage}
              cableSpec={calcData.cableSpec}
              chimneySpec={calcData.chimney}
              heaterClearance={calcData.heaterClearance}
              overrides={overrides}
            />
          </div>
        )}
      </Suspense>

      {renderError && (
        <div className="builder-inline-alert" role="alert">
          {renderError}
        </div>
      )}
    </main>
  );
}
